import config from '../config';
import { secureStorage } from './secureStorage';
import { WSEventType, WSMessage } from '../types';

type WSCallback = (message: WSMessage) => void;

class WebSocketService {
  private ws: WebSocket | null = null;
  private url: string = '';
  private listeners: Map<string, Set<WSCallback>> = new Map();
  private reconnectAttempts: number = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private isIntentionallyClosed: boolean = false;
  private pingInterval: ReturnType<typeof setInterval> | null = null;

  async connect(negotiationId?: string): Promise<void> {
    const path = negotiationId
      ? `/chat/${negotiationId}/`
      : '/notifications/';

    let token = '';
    try {
      const raw = await secureStorage.getItem('tokens');
      token = raw ? JSON.parse(raw).access || '' : '';
    } catch {}
    const separator = path.includes('?') ? '&' : '?';
    this.url = `${config.WS_BASE_URL}${path}${token ? `${separator}token=${encodeURIComponent(token)}` : ''}`;
    this.isIntentionallyClosed = false;
    this.reconnectAttempts = 0;
    this._connect();
  }

  private _connect(): void {
    if (this.ws) {
      this.ws.close();
    }

    this.ws = new WebSocket(this.url);

    this.ws.onopen = () => {
      console.log('WS: Connected');
      this.reconnectAttempts = 0;
      this._startPing();
      this._emit('connection', {
        type: 'connection' as any,
        payload: { status: 'connected' },
      });
    };

    this.ws.onmessage = (event: MessageEvent) => {
      try {
        const raw = JSON.parse(event.data);
        const message: WSMessage = raw.type
          ? raw
          : {
              type: raw.message_text ? WSEventType.CHAT_MESSAGE : ('notification' as any),
              payload: raw,
            };
        this._emit(message.type, message);
        this._emit('*', message); // wildcard listeners
      } catch (e) {
        console.warn('WS: Failed to parse message', e);
      }
    };

    this.ws.onerror = (error: Event) => {
      console.warn('WS: Error', error);
      this._emit('error', {
        type: 'error' as any,
        payload: { error },
      });
    };

    this.ws.onclose = (event: CloseEvent) => {
      console.log('WS: Closed', event.code, event.reason);
      this._stopPing();
      this._emit('connection', {
        type: 'connection' as any,
        payload: { status: 'disconnected' },
      });

      if (!this.isIntentionallyClosed) {
        this._scheduleReconnect();
      }
    };
  }

  private _scheduleReconnect(): void {
    if (this.reconnectAttempts >= config.WS_RECONNECT_MAX_ATTEMPTS) {
      console.log('WS: Max reconnect attempts reached');
      this._emit('reconnect_failed', {
        type: 'reconnect_failed' as any,
        payload: {},
      });
      return;
    }

    const delay = Math.min(
      config.WS_RECONNECT_BASE_DELAY * Math.pow(2, this.reconnectAttempts),
      config.WS_RECONNECT_MAX_DELAY,
    );

    console.log(`WS: Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts + 1})`);
    this.reconnectAttempts++;

    this.reconnectTimer = setTimeout(() => {
      this._connect();
    }, delay);
  }

  private _startPing(): void {
    this._stopPing();
    this.pingInterval = setInterval(() => {
      this.send({ type: 'ping' as any, payload: {} });
    }, 30000);
  }

  private _stopPing(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  send(message: WSMessage): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn('WS: Cannot send, not connected');
    }
  }

  sendChatMessage(content: string, replyToId?: string): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ message_text: content, reply_to: replyToId }));
      return;
    }
    console.warn('WS: Cannot send, not connected');
  }

  sendTyping(): void {
    this.send({
      type: WSEventType.TYPING,
      payload: {},
    });
  }

  sendReadReceipt(messageIds: string[]): void {
    this.send({
      type: WSEventType.MESSAGE_READ,
      payload: { message_ids: messageIds },
    });
  }

  on(event: string, callback: WSCallback): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);

    // Return unsubscribe function
    return () => {
      this.listeners.get(event)?.delete(callback);
    };
  }

  private _emit(event: string, message: WSMessage): void {
    this.listeners.get(event)?.forEach((cb) => {
      try {
        cb(message);
      } catch (e) {
        console.warn('WS: Listener error', e);
      }
    });
  }

  disconnect(): void {
    this.isIntentionallyClosed = true;
    this._stopPing();

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.listeners.clear();
  }

  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}

// Singleton instances
export const chatWS = new WebSocketService();
export const updatesWS = new WebSocketService();
